<template>
  <section class="TileDisplay">
    <h2 class="TileDisplay-title">
      {{ title }}
    </h2>

    <ScrollableRow class="TileDisplay-content" :ariaLabel="`${title} tiles`">
      <component
        :is="component"
        v-for="tile of tiles"
        :key="`tile-${'step' in tile ? tile.step : 'author' in tile ? tile.author : ''}`"
        v-bind="tile"
        class="TileDisplay-tile"
      />
    </ScrollableRow>

    <div v-if="disclaimer" class="TileDisplay-disclaimer">
      <p class="TileDisplay-disclaimerText">
        {{ disclaimer }}
      </p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import BenefitTile from '@cvo/ui/components/molecules/Tiles/BenefitTile.vue';
import TestimonialsTile from '@cvo/ui/components/molecules/Tiles/TestimonialsTile.vue';
import ScrollableRow from '@cvo/ui/components/atoms/ScrollableRow/ScrollableRow.vue';
import type { TileDisplayProps } from './TileDisplay.model';

const props = defineProps<TileDisplayProps>();

const component = computed(() => {
  switch(props.type) {
    case 'benefits':
      return BenefitTile;
    case 'testimonials':
      return TestimonialsTile;
    default:
      return null;
  }
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .TileDisplay {
    display: flex;
    flex-direction: column;
    padding: vars.$patterns-tiledisplay-lg-md-padding-top-bottom vars.$patterns-tiledisplay-lg-md-padding-left-right;
    border: vars.$patterns-tiledisplay-border-stroke solid vars.$patterns-tiledisplay-border-color;
    border-radius: vars.$patterns-tiledisplay-border-radius;
    background-color: vars.$patterns-tiledisplay-bg-color;
    gap: vars.$dimensions-d-8;

    @include responsive.mobile {
      padding: vars.$patterns-tiledisplay-sm-padding-top-bottom vars.$patterns-tiledisplay-sm-padding-left-right;
      gap: vars.$dimensions-d-5;
    }

    &-title {
      @include text.heading('h2');
      margin: 0;
      color: vars.$patterns-tiledisplay-title-color;
      white-space: pre-wrap;

      @include responsive.mobile {
        @include text.heading('h4');
      }
    }

    &-content {
      gap: vars.$dimensions-d-8;
      scroll-snap-type: x proximity;
      scroll-padding-inline-start: vars.$dimensions-d-8;

      @include responsive.mobile {
        gap: vars.$dimensions-d-5;
        scroll-padding-inline-start: vars.$dimensions-d-5;
      }
    }

    &-tile {
      flex: 1 1 0px;
      scroll-snap-align: start;
    }

    &-disclaimer {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
    }

    &-disclaimerText {
      @include text.text('sm');
      margin: 0;
      color: vars.$patterns-tiledisplay-disclaimer-color;
      flex: 1;
    }
  }
}
</style>
